import { openFile } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  IntakeForm,
  IntakeFormRequest,
  IntakeFormRequestInput,
  IntakeFormSubmissionPdfInput,
  OnboardingRequestResponse,
  RecoverSubmissionsResponse
} from "./types/onboarding-request.type";

const onBoardingRequestsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getIntakeForms: builder.query<
      IntakeForm[],
      {
        practice: number | undefined;
        clinic: number | undefined;
        is_active?: boolean;
        query?: string;
      }
    >({
      query: (params) => ({
        url: `/intake-forms/`,
        method: "GET",
        params
      }),
      providesTags: ["IntakeForms"]
    }),
    syncIntakeForms: builder.mutation<void, void>({
      query: () => ({
        url: `/intake-forms/sync/`,
        method: "POST"
      }),
      invalidatesTags: ["IntakeForms"]
    }),
    getAllIntakeForms: builder.query<IntakeForm[], void>({
      query: () => ({
        url: `/intake-forms/`,
        method: "GET"
      }),
      providesTags: ["IntakeForms"]
    }),
    updateIntakeForm: builder.mutation<
      IntakeForm,
      { id: number; is_active: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/intake-forms/${id}/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["IntakeForms"]
    }),
    getIntakeFormRequests: builder.query<
      OnboardingRequestResponse,
      { patientId: string; ordering?: string; appointment?: string }
    >({
      query: ({ patientId, ordering = "-created_at", ...params }) => ({
        url: `/patients/${patientId}/intake-form-requests/`,
        method: "GET",
        params: { ordering, ...params }
      }),
      providesTags: ["OnboardingFormRequest"]
    }),
    downloadIntakeFormSubmissionPdf: builder.mutation<
      Blob,
      IntakeFormSubmissionPdfInput
    >({
      query(request) {
        return {
          url: `/patients/${request.patientId}/intake-form-requests/${request.requestId}/submissions/${request.submissionId}/pdf/`,
          method: "GET",
          headers: {
            Accept: "*/*"
          },
          responseHandler: async (response) => {
            const blob = await response.blob();

            if (!request.doNotSaveFile) {
              const fileUrl = window.URL.createObjectURL(blob);

              openFile(fileUrl);
            }

            return blob;
          },
          cache: "no-cache"
        };
      }
    }),
    createIntakeFormRequest: builder.mutation<
      IntakeFormRequest,
      IntakeFormRequestInput
    >({
      query: (intakeRequestData) => ({
        url: `/patients/${intakeRequestData.patient}/intake-form-requests/`,
        method: "POST",
        body: intakeRequestData
      }),
      invalidatesTags: ["OnboardingFormRequest"]
    }),
    sendReminderIntakeFormRequest: builder.mutation<
      void,
      {
        patientId: number;
        intakeId: number;
        notification_method?: number;
        notification_contact?: string;
      }
    >({
      query: ({ patientId, intakeId, ...body }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/reminder/`,
        method: "PATCH",
        body
      })
    }),
    recoverIntakeFormSubmissions: builder.mutation<
      RecoverSubmissionsResponse,
      { patientId: number; intakeId: number }
    >({
      query: ({ patientId, intakeId }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/recover-submissions/`,
        method: "POST",
        body: { limit: 100 }
      }),
      invalidatesTags: [
        "OnboardingFormRequest",
        "IntakeFormSubmissionsReport"
      ]
    }),
    cancelIntakeFormRequest: builder.mutation<
      void,
      { patientId: number; intakeId: number }
    >({
      query: ({ patientId, intakeId }) => ({
        url: `/patients/${patientId}/intake-form-requests/${intakeId}/cancel/`,
        method: "PATCH"
      }),
      invalidatesTags: [
        "IntakeForms",
        "OnboardingFormRequest",
        "IntakeFormSubmissionsReport"
      ]
    })
  })
});

export const {
  useGetIntakeFormsQuery,
  useGetIntakeFormRequestsQuery,
  useCreateIntakeFormRequestMutation,
  useDownloadIntakeFormSubmissionPdfMutation,
  useSendReminderIntakeFormRequestMutation,
  useRecoverIntakeFormSubmissionsMutation,
  useCancelIntakeFormRequestMutation,
  useUpdateIntakeFormMutation,
  useSyncIntakeFormsMutation,
  useGetAllIntakeFormsQuery
} = onBoardingRequestsApi;
